import type { BlockType } from '@unifyapps/defs/types/block';
import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import { useEffect, useMemo } from 'react';
import DataSourceHelper from '../helper/DataSourceHelper';
import DependencyFlowHelper from '../helper/DependencyFlowHelper';
import type { DataSourceControllerActions } from '../slices/DataSourceControllerSlice/types';
import {
  type GlobalAppStateStoreType,
  useGetGlobalAppStateStoreState,
  useGlobalAppStateStore,
} from '../stores/app/GlobalAppStateStore';
import {
  useGetAppDataSourceRecordStoreState,
  useGetDataSourceRecords,
} from '../stores/DataSourceRecordStore';
import type { GlobalStateStoreType } from '../stores/GlobalStateStore';
import {
  useGetDataSourceStateActions,
  useGetGlobalStateStoreStateFromDataSourceId,
} from '../stores/getters/state';
import { getIsDataSourceAnEntityType } from '../utils/entityDataSource';

function useBaseTriggerAutomaticDataSources({
  dataSourceIds = [],
  getGlobalStateStoreState,
  getDataSourceStateActions,
  getDataSourceRecords,
}: {
  dataSourceIds: BlockType['dataSourceIds'];
  getGlobalStateStoreState: (
    dataSourceId: string,
  ) => GlobalStateStoreType | GlobalAppStateStoreType | undefined;
  getDataSourceStateActions: (dataSourceId: string) => DataSourceControllerActions;
  getDataSourceRecords: () => Record<string, DataSourceEntity | undefined>;
}) {
  const automaticDataSourceIds = useMemo(() => {
    const dataSourceRecords = getDataSourceRecords();
    // it is possible that we might have includes manual data source id in dataSourceIds
    return dataSourceIds.filter((id) => {
      // If a data source is accessed somewhere but is later deleted.
      if (dataSourceRecords[id]) {
        return !DataSourceHelper.isManualDataSource(dataSourceRecords[id]);
      }

      // If the data source is of entity type, include it to trigger.
      // This ensures proper handling for cases like Data Table with an entity as source.
      if (getIsDataSourceAnEntityType(id)) {
        return true;
      }

      return false;
    });
  }, [dataSourceIds, getDataSourceRecords]);

  const nonStagedDataSourceIds = useMemo(() => {
    return automaticDataSourceIds.filter((id) => {
      // this function automatically chooses correct store based on id and then return the correct global state store
      const dsDependencyFlow = getGlobalStateStoreState(id)?.dataSourceDependencyFlow;
      if (!dsDependencyFlow) {
        // we need to trigger the entity data source to get the data always, it will not have it's record stored in the data source record store
        if (getIsDataSourceAnEntityType(id)) {
          return true;
        }
        console.error('dependency flow not found for data source', id);
        return false;
      }

      return DependencyFlowHelper.isDependencyUnstaged(dsDependencyFlow, id);
    });
  }, [automaticDataSourceIds, getGlobalStateStoreState]);

  // now because we need to move the data source to staging area, we need to know which data source to send to which store, so here we figure that out
  const { pageNonStagedDataSourceIds, appNonStagedDataSourceIds } = useMemo(() => {
    const records = getDataSourceRecords();
    return nonStagedDataSourceIds.reduce<{
      pageNonStagedDataSourceIds: string[];
      appNonStagedDataSourceIds: string[];
    }>(
      (acc, id) => {
        const dataSource = records[id];
        const isAppDataSource = DataSourceHelper.isAppDataSource(dataSource);
        acc[isAppDataSource ? 'appNonStagedDataSourceIds' : 'pageNonStagedDataSourceIds'].push(id);
        return acc;
      },
      { pageNonStagedDataSourceIds: [], appNonStagedDataSourceIds: [] },
    );
  }, [nonStagedDataSourceIds, getDataSourceRecords]);

  useEffect(() => {
    if (pageNonStagedDataSourceIds.length) {
      // getDataSourceStateActions automatically chooses correct store based on id and then return the correct data source actions from store
      const dataSource = getDataSourceStateActions(pageNonStagedDataSourceIds[0]);
      dataSource?.addDependencyFlows(
        DependencyFlowHelper.createStagingDependencyFlow({
          ids: pageNonStagedDataSourceIds,
        }),
      );
    }
  }, [pageNonStagedDataSourceIds, getDataSourceStateActions]);

  useEffect(() => {
    if (appNonStagedDataSourceIds.length) {
      const dataSource = getDataSourceStateActions(appNonStagedDataSourceIds[0]);
      dataSource?.addDependencyFlows(
        DependencyFlowHelper.createStagingDependencyFlow({
          ids: appNonStagedDataSourceIds,
        }),
      );
    }
  }, [appNonStagedDataSourceIds, getDataSourceStateActions]);
}

export function useTriggerAutomaticDataSources({
  dataSourceIds = [],
}: {
  dataSourceIds: BlockType['dataSourceIds'];
}) {
  const { getGlobalStateStoreStateFromDataSourceId } =
    useGetGlobalStateStoreStateFromDataSourceId();
  const { getDataSourceStateActions } = useGetDataSourceStateActions();
  const { getDataSourceRecords } = useGetDataSourceRecords();

  useBaseTriggerAutomaticDataSources({
    dataSourceIds,
    getGlobalStateStoreState: getGlobalStateStoreStateFromDataSourceId,
    getDataSourceStateActions,
    getDataSourceRecords,
  });
}

export function useTriggerGlobalAutomaticDataSources({
  dataSourceIds = [],
}: {
  dataSourceIds: BlockType['dataSourceIds'];
}) {
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const appDataSourceActions = useGlobalAppStateStore().hooks.useActions().dataSource;
  const { getAppDataSourceRecordStoreState } = useGetAppDataSourceRecordStoreState();

  useBaseTriggerAutomaticDataSources({
    dataSourceIds,
    getGlobalStateStoreState: () => getGlobalAppStateStoreState(),
    getDataSourceStateActions: () => appDataSourceActions,
    getDataSourceRecords: () => getAppDataSourceRecordStoreState().dataSources,
  });
}
